import * as React from 'react'
import PropTypes from 'prop-types'
import { dataProps } from '@ds/react-utils'
import { useDsUiDataProps, useThemeStyles } from '../../theming'
import styles from './styles'

export interface TextEllipsisProps {
  /**
   * The text to display.
   */
  children: string
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * The number of lines to allow before text gets cut off.
   */
  lines?: number
  /**
   * Text representing advisory information related to the element. Defaults to the string passed in as `children`.
   */
  title?: string
}

/**
 * The `TextEllipsis` component uses the newer `line-clamp` CSS property to achieve multi-line ellipsis. This works in all modern browsers using a combination of [prefixed properties](https://css-tricks.com/almanac/properties/l/line-clamp/).
 */
export function TextEllipsis({
  children,
  lines = 1,
  title,
  ...restProps
}: TextEllipsisProps) {
  const sx = useThemeStyles(styles, { lines })
  const dsUiDataProps = useDsUiDataProps(TextEllipsis)

  return (
    <span
      {...dsUiDataProps}
      {...dataProps(restProps)}
      css={sx.textEllipsis}
      /**
       * Support RTL w/ mixed content
       * @see https://rtlstyling.com/posts/rtl-styling#4.-text-truncation
       */
      dir="auto"
      title={title || children}
    >
      {children}
    </span>
  )
}

TextEllipsis.propTypes = {
  children: PropTypes.string.isRequired,
  'data-.*': PropTypes.string,
  lines: PropTypes.number,
  title: PropTypes.string,
}

TextEllipsis.displayName = 'TextEllipsis'
